/**
 * Curve-specific edit operations — handle type, extend, toggle cyclic,
 * delete points. Curves don't flow through `runEditOp` because their
 * data model is not `PolygonTopology`-shaped (they use `CurveData` +
 * `curveEditVertices`).
 */

import {
  buildCurveEditVertices,
  deleteCurvePoints as deleteCurvePointsOp,
  extendCurve as extendCurveOp,
  setCurveHandleType as setCurveHandleTypeOp,
} from "../../modeling/curveOps.ts";
import { hasVertexSelection } from "../../modeling/editSelection.ts";
import type { CurveHandleType } from "../../modeling/types.ts";
import { regenerateCurveGeometryInMap } from "../curveRegen.ts";
import { captureSnapshot, undoManager } from "../storeUndo.ts";
import type { Get_, Set_ } from "./core.ts";

export function createCurveOps(set: Set_, get: Get_) {
  return {
    setCurveHandleType: (type: CurveHandleType) => {
      const em = get().editMode;
      if (!em?.isCurve || !hasVertexSelection(em)) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj?.curveData) return;

      const updated = setCurveHandleTypeOp(
        obj.curveData,
        em.selectedVertices,
        type,
      );
      set({
        sceneObjects: regenerateCurveGeometryInMap(
          get().sceneObjects,
          em.objectId,
          updated,
        ),
        editMode: { ...em, curveEditVertices: buildCurveEditVertices(updated) },
      });
    },

    extendCurve: () => {
      const state = get();
      const em = state.editMode;
      if (!em?.isCurve) return;
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj?.curveData) return;

      const cancelSnap = captureSnapshot(state);
      const preSelection = new Set(em.selectedVertices);

      const result = extendCurveOp(obj.curveData, em.selectedVertices);
      if (!result) return;

      set({
        sceneObjects: regenerateCurveGeometryInMap(
          state.sceneObjects,
          em.objectId,
          result.curveData,
        ),
        editMode: {
          ...em,
          selectedVertices: new Set([result.newPointEditIndex]),
          curveEditVertices: buildCurveEditVertices(result.curveData),
        },
      });

      // Enter translate so the user can drag the new point into position
      const postSnap = captureSnapshot(get());
      undoManager.push(cancelSnap);
      get().enterCommand("translate", {
        preEditSelection: preSelection,
        preSnapshot: postSnap,
        cancelSnapshot: cancelSnap,
      });
    },

    toggleCurveCyclic: () => {
      const em = get().editMode;
      if (!em?.isCurve) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj?.curveData) return;

      const updated = { ...obj.curveData, cyclic: !obj.curveData.cyclic };
      set({
        sceneObjects: regenerateCurveGeometryInMap(
          get().sceneObjects,
          em.objectId,
          updated,
        ),
      });
    },

    deleteCurvePoints: () => {
      const em = get().editMode;
      if (!em?.isCurve || !hasVertexSelection(em)) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj?.curveData) return;

      const updated = deleteCurvePointsOp(obj.curveData, em.selectedVertices);
      if (!updated) return;

      undoManager.push(captureSnapshot(get()));
      set({
        sceneObjects: regenerateCurveGeometryInMap(
          get().sceneObjects,
          em.objectId,
          updated,
        ),
        editMode: {
          ...em,
          selectedVertices: new Set(),
          curveEditVertices: buildCurveEditVertices(updated),
        },
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
    },
  };
}
